/**
 * Trade-In Report Renderer
 *
 * Service-facing table of used vehicles taken in on trade (from the location's
 * Nut Sheet) so Service can create recon repair orders. One row per trade —
 * a two-trade deal shows two rows tagged T1/T2. The backend exposes ONLY the
 * whitelisted trade fields (no gross/ACV/retail/F&I/customer/salesperson), so
 * this renderer never has sensitive data to hide.
 *
 * Designed for printing (Service posts it / works from it).
 */

import React from 'react';
import { WrenchScrewdriverIcon } from '@heroicons/react/24/outline';

/** One trade row — mirrors the backend `TradeInRow`. */
export interface TradeInRow {
  date: string;
  salesManager: string | null;
  tradeNumber: 1 | 2;
  stockNumber: string | null;
  year: number | null;
  make: string | null;
  model: string | null;
  miles: number | null;
  plan: string | null;
  planLabel: string | null;
  comment: string | null;
}

export interface TradeInReportData {
  locationId: number;
  startDate: string;
  endDate: string;
  totalTrades: number;
  rows: TradeInRow[];
}

function formatMiles(miles: number | null): string {
  if (miles === null || miles === undefined) return '—';
  return miles.toLocaleString('en-US');
}

/** Render a YYYY-MM-DD date as a short, timezone-safe label. */
function formatDate(date: string): string {
  const [y, m, d] = date.split('-').map((s) => parseInt(s, 10));
  if (!y || !m || !d) return date;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function dash(v: string | null): string {
  return v && v.trim() !== '' ? v : '—';
}

export const TradeInReport: React.FC<{ data: TradeInReportData }> = ({ data }) => {
  const rows = data.rows || [];

  if (rows.length === 0) {
    return (
      <div className="text-center py-12">
        <WrenchScrewdriverIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
        <p className="text-gray-500">No trade-ins found for the selected date range.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between print:mb-2">
        <p className="text-sm text-gray-600">
          <span className="font-semibold text-gray-900">{data.totalTrades}</span>{' '}
          trade{data.totalTrades === 1 ? '' : 's'} taken in
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-700 text-white print:bg-gray-200 print:text-black">
              <th className="px-3 py-2 text-left font-semibold">Date</th>
              <th className="px-3 py-2 text-left font-semibold">Sales Mgr</th>
              <th className="px-3 py-2 text-center font-semibold">Trade</th>
              <th className="px-3 py-2 text-left font-semibold">Stock #</th>
              <th className="px-3 py-2 text-right font-semibold">Year</th>
              <th className="px-3 py-2 text-left font-semibold">Make</th>
              <th className="px-3 py-2 text-left font-semibold">Model</th>
              <th className="px-3 py-2 text-right font-semibold">Miles</th>
              <th className="px-3 py-2 text-left font-semibold">Plan</th>
              <th className="px-3 py-2 text-left font-semibold">Comments</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const prev = rows[i - 1];
              const newDay = !prev || prev.date !== row.date;
              return (
                <tr
                  key={`${row.date}-${row.stockNumber ?? 'nostk'}-${row.tradeNumber}-${i}`}
                  className={`border-b border-gray-200 ${i % 2 === 1 ? 'bg-gray-50 print:bg-white' : ''} ${
                    newDay ? 'border-t-2 border-t-gray-300' : ''
                  }`}
                >
                  <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                    {newDay ? formatDate(row.date) : ''}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-700">{dash(row.salesManager)}</td>
                  <td className="px-3 py-2 text-center">
                    <span
                      className={`inline-block px-1.5 py-0.5 rounded text-xs font-semibold ${
                        row.tradeNumber === 1
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}
                    >
                      T{row.tradeNumber}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap font-medium text-gray-900">
                    {dash(row.stockNumber)}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-700">{row.year ?? '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-700">{dash(row.make)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-700">{dash(row.model)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap text-gray-700">
                    {formatMiles(row.miles)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                    {row.plan ? (
                      <span title={row.planLabel ?? undefined}>{row.plan}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-700">{dash(row.comment)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
